import { onBeforeUnmount, watch } from 'vue'

/**
 * Wire Escape-to-close on the app's overlay modals.
 *
 * These modals render as plain `<div>` overlays rather than a native
 * `<dialog>`, so the browser gives them no Escape handling for free. This
 * composable attaches a document-level `keydown` listener *only while the
 * modal is open* and invokes `onEscape` (typically the close emit) when
 * Escape is pressed. The listener is removed as soon as the modal closes and
 * is always torn down on unmount, so it never leaks or fires for a hidden
 * modal.
 *
 * @param isOpen  Getter returning the modal's visibility (e.g. `() => props.show`).
 * @param onEscape Callback to run when Escape is pressed while open.
 */
export function useEscapeToClose(isOpen: () => boolean, onEscape: () => void): void {
  const handler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') onEscape()
  }

  watch(
    isOpen,
    (open) => {
      if (open) {
        document.addEventListener('keydown', handler)
      } else {
        document.removeEventListener('keydown', handler)
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(() => document.removeEventListener('keydown', handler))
}
